// src/services/http.js
import axios from "axios";
import {
  getAccessToken,
  refreshAccessToken,
  clearAuth,
  getAuthRevision,
} from "./authentication.service";
import { reportError } from "./errorBus";

// Raíz del backend; sin slash final
const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// Instancia principal de Axios para la API
export const http = axios.create({
  baseURL: `${API_ROOT}/api`,
  headers: {
    Accept: "application/json",
    // No fijes Content-Type manualmente; axios lo añade cuando corresponde
  },
  withCredentials: true, // necesario para enviar/recibir cookies (refresh)
});

// ---------- Request interceptor ----------
function isAuthRequest(config) {
  const path = new URL(http.getUri(config), window.location.origin).pathname;
  return path.startsWith("/api/auth/");
}

http.interceptors.request.use((config) => {
  // Asegura cookies cross-site (refresh HttpOnly)
  config.withCredentials = true;

  const headers = axios.AxiosHeaders.from(config.headers);
  if (isAuthRequest(config)) {
    headers.delete("Authorization");
    config.headers = headers;
    return config;
  }
  const revision = getAuthRevision();
  if (config._authRevision !== undefined && config._authRevision !== revision)
    throw new axios.CanceledError("La sesión cambió durante la solicitud.");
  config._authRevision = revision;
  const tok = getAccessToken?.();
  config._accessToken = tok;
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
  else headers.delete("Authorization");
  config.headers = headers;
  return config;
});

// ---------- Response interceptor ----------
http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const resp = error?.response;
    const original = error?.config;
    if (axios.isCancel(error) || (original && isAuthRequest(original))) return Promise.reject(error);

    // Servidor devolvió HTML inesperado (por ejemplo, error Tomcat / proxy)
    if (!original?.silentError && resp && typeof resp.data === "string" && resp.data.startsWith("<!DOCTYPE")) {
      reportError(error, "El servidor no pudo procesar la solicitud. Inténtalo de nuevo más tarde.");
      return Promise.reject(error);
    }

    const status = resp?.status;

    // 1) Manejo de expiración de token:
    //    si backend devuelve 401 para JWT inválido/expirado,
    //    intentamos un refresh UNA sola vez y reintentamos la petición original.
    if (
      status === 401 &&
      original &&
      original._accessToken
    ) {
      if (original._authRevision !== getAuthRevision()) return Promise.reject(error);
      if (original._retry) {
        clearAuth();
        return Promise.reject(error);
      }
      original._retry = true;

      try {
        // A late 401 can reuse a token already renewed by another request.
        if (getAccessToken() === original._accessToken) await refreshAccessToken();
        if (original._authRevision !== getAuthRevision()) return Promise.reject(error);
        return http(original);
      } catch (e) {
        // Sin sesión; deja que ProtectedRoute redirija a /login
        return Promise.reject(error);
      }
    }

    // Los errores de la API se presentan en el aviso global; 401 conserva el flujo de sesión.
    if (
      !original?.silentError && (!resp ||
      status >= 500 ||
      (status >= 400 && status !== 401))
    ) {
      reportError(error);
    }

    return Promise.reject(error);
  }
);
